import { useState, useReducer, useContext, useEffect, useRef } from "react";
import styled, { css } from "styled-components";
import ReactHtmlParser from "react-html-parser";
import { reducer } from "Modules";
import { EventContext, UserContext, AnalyticsContext } from "context";
import { ButtonPrimary } from "Components/Buttons";
import { handleErrors, Field } from "Components/Form";

export const RegistrationForm = ({
  formPart,
  formHeading,
  formLegal,
  submitButton,
  setIneligible,
  focus,
  setLoading,
  setRef,
}) => {
  const { event } = useContext(EventContext);
  const { writeToUserSubmissionDoc } = useContext(UserContext);
  const { logLeadSubmission, logClick } = useContext(AnalyticsContext);

  useEffect(() => {
    const scrollRef = document.getElementById("FNAME");
    if (scrollRef) {
      setRef(scrollRef);
    }
  }, []);

  const [registration, setRegistration] = useReducer(reducer, {});
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const fields = event.register.fields;

  const eligibleZipCodes = JSON.parse(event.register.zipCodes);
  const zips = eligibleZipCodes.map((item) => item.zip);

  const linkClickHandler = (url, label, category) => {
    logClick({ label, url }, category);
  };

  const handleChange = (value, name) => {
    setRegistration(value);
    delete errors[name];
    setErrors(errors);
  };

  useEffect(() => {
    if (errors) {
      const [errorId] = Object.keys(errors);
      let focusRef = document.getElementById(errorId);
      focusRef?.focus();
    }
  }, [errors]);

  useEffect(() => {
    setSubmitting(false);
  }, [submitButton]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    let errorObject = handleErrors(fields, registration, formPart);

    if (!errorObject) {
      setSubmitting(true);
      setLoading(true);

      registration.formPart = formPart;
      await writeToUserSubmissionDoc(registration);
      logLeadSubmission({ formPart, registration });
      setTimeout(() => setLoading(false), 2000);
    } else {
      setSubmitting(false);
      setErrors(errorObject);
    }
  };

  return (
    <>
      {fields && (
        <RegForm onSubmit={handleSubmit}>
          <Form>
            {fields
              .sort((a, b) => a.order - b.order)
              .filter((field) => {
                return field.formPart === formPart;
              })
              .map((field, index) => {
                let mobileSize = "100%";
                let desktopSize = "97%";
                if (field.span === "half") desktopSize = "calc(48% - 2px)";

                if (
                  field.span !== "full" &&
                  (field.spanMobile === "half" || !field.spanMobile)
                )
                  mobileSize = "calc(48% - 4px)";

                return (
                  <FieldContainer
                    sizeDesktop={desktopSize}
                    sizeMobile={mobileSize}
                    fullSize={field.span === "full"}
                    key={index}
                    order={field.order}
                    optin={field.name === "OPTIN"}
                  >
                    {field.type !== "optIn" && !!field.label && (
                      <Label
                        as="label"
                        htmlFor={field.label}
                        tabIndex={field.name === "OPTIN" && 0}
                        optin={field.name === "OPTIN"}
                      >
                        {field.label}
                      </Label>
                    )}
                    <Field
                      field={field}
                      value={registration[field.name]}
                      onChange={(value) => handleChange(value, field.name)}
                      // autoFocus={field.name === "FNAME" && focus}
                      error={{
                        status: errors[field.name],
                        message: errors[field.name]?.message,
                      }}
                    />
                  </FieldContainer>
                );
              })}

            {formHeading && formLegal && (
              <>
                <FormLegal
                  tabIndex={0}
                  onClick={() =>
                    linkClickHandler(
                      "USC/MAX privacy policy",
                      "USC/MAX privacy policy",
                      "external"
                    )
                  }
                >
                  *If you marked “yes”, above, UScellular will use the email and
                  phone number provided to send promotional emails, automated
                  text messages and calls.{" "}
                  <span>Std. msg & data rates apply to texts. </span>
                  Consent to receive texts is not required to buy goods or
                  services. You will receive approximately 1 text. You can
                  change your preference for text messages, emails and calls
                  regarding latest offers at any time. Use of your information
                  shall be governed by UScellular’s{" "}
                  <a
                    href="https://www.uscellular.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="button to open link in a new window to read US Cellular Privacy Policy"
                  >
                    Privacy Policy
                  </a>
                  .
                </FormLegal>
                <FormLegal
                  tabIndex={0}
                  onClick={() =>
                    linkClickHandler(
                      "USC/MAX privacy policy",
                      "USC/MAX privacy policy",
                      "external"
                    )
                  }
                >
                  {ReactHtmlParser(formHeading)}
                </FormLegal>
                <FormLegal
                  aria-label="Official Sweepstakes Rules link opens in a new window"
                  onClick={() =>
                    linkClickHandler(
                      "https://bit.ly/3vrf2Hn",
                      "Sweepstakes Rules",
                      "internal"
                    )
                  }
                >
                  {ReactHtmlParser(formLegal)}
                </FormLegal>
              </>
            )}
          </Form>

          {submitting ? (
            <RegisterButton>Loading</RegisterButton>
          ) : (
            <RegisterButton
              type="submit"
              aria-label="Enter button to register for sweepstakes with Chase Rice"
              onClick={() =>
                linkClickHandler("Register button", "Registration", "internal")
              }
            >
              {submitButton ? submitButton : "Submit"}
            </RegisterButton>
          )}
        </RegForm>
      )}
    </>
  );
};

const FormTitle = styled.div`
  margin: 12px 0;
  color: #000;
  font-size: 12px;
  font-weight: 300;
  order: 98;
  /* font-family: "AntennaLight"; */
`;

const Form = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 10px;
  row-gap: 20px;
  margin: 20px 0px;
  width: 100%;

  @media (max-width: 500px) {
    gap: 25px;
    row-gap: 25px;
  }
`;

const RegForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: start;
  padding-bottom: 50px;
  @media ${(props) => props.theme.maxMedia.medium} {
    padding-bottom: 0;
  }
`;

const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 1 ${(props) => props.sizeDesktop};
  ${(props) => props.order && `order: ${props.order};`}
  width: 100%;

  @media ${(props) => props.theme.maxMedia.medium} {
    flex: 0 1 ${(props) => props.sizeMobile};
  }
  ${({ optin }) =>
    optin &&
    css`
      div#OPTIN {
        text-align: center;
        margin: auto;
      }
    `}
`;

const Label = styled.label`
  color: #000;
  font-size: 14px;
  margin-bottom: 5px;
  text-align: left;
  ${({ optin }) =>
    optin &&
    css`
      text-align: center;
      margin: auto;
    `}
`;

const FormLegal = styled.div`
  font-size: 14px;
  line-height: 1.5;
  margin: -7px auto 10px;
  order: 99;
  color: #000;
  text-align: center;
  a,
  a:visited {
    color: #322ffd;
    text-decoration: underline;
    font-family: "BrandonBld";
  }
  span {
    font-family: "BrandonBld";
  }
`;

const RegisterButton = styled(ButtonPrimary)`
  text-align: center;
  margin: auto;
  margin-bottom: 10px;
  width: 40%;
  @media (max-width: 950px) {
    margin: 25px auto 25px;
    width: 50%;
    padding: 10px 0;
    font-size: 16px;
    align-self: center;
  }
`;
